import React from "react";
import { getReadMoreHeight } from "./GridRow";
import ReadMore from "../../components/UI/ReadMore/ReadMore";
import { ThemeConsumer, ThemeProvider, DefaultTheme } from "styled-components";
import { ErrorBoundary } from "../../errors";
import GridControl from "./GridControl";
import FadeLeftWrap from "../../components/UI/Animate/FadeInLeft";
import FadeRightWrap from "../../components/UI/Animate/FadeInRight";
import { CONTROL_TYPES, READ_MORE_HEIGHTS } from "../../typings/grid";

export interface AreaStyles
{
    "color"?: string;
    "background-color"?: string;
    "background-image"?: string;
}

export type AREA_CELL_ALIGNMENT = "top" | "center" | "bottom";
export type COLUMN_ANIM = "none" | "fadeLeft" | "fadeRight";

export interface AreaConfig
{
    alignmentY: AREA_CELL_ALIGNMENT;
    readMore: READ_MORE_HEIGHTS;
    "animatecolumn"?: COLUMN_ANIM;
    unpublished: boolean;
}

export interface GridAreaValue
{
    controls: CONTROL_TYPES[];
    grid: number;
    config: AreaConfig;
    styles: AreaStyles;
}

const GridArea: React.StatelessComponent<GridAreaValue> = (props) =>
{
    const { controls, grid, config, styles } = props;

    const getRowTheme = (rowTheme: DefaultTheme) =>
    {
        if (styles)
        {
            rowTheme.color = styles["color"] ? styles["color"] : rowTheme.color;
            rowTheme.backgroundColor = styles["background-color"] ? styles["background-color"] : rowTheme.backgroundColor;
        }

        return rowTheme;
    };

    const getGridSize = () =>
    {
        switch (grid)
        {
            case 3:
                return "grid-area grid-area--fourth";
            case 4:
                return "grid-area grid-area--third";
            case 6:
                return "grid-area grid-area--half";
            default:
                return "grid-area grid-area--full";
        }
    };

    const getColumnConfig = () =>
    {
        if (config && config.alignmentY)
        {
            switch (config.alignmentY.toLowerCase())
            {
                case "center":
                    return "h-full flex justify-center flex-col";
                case "bottom":
                    return "h-full flex justify-center flex-col";
                default:
                    return "";
            }
        }
    };

    const getStyles = (): React.CSSProperties =>
    {
        const cssProps: React.CSSProperties = {};
        if (styles)
        {
            cssProps.color = styles["color"];

            cssProps.backgroundColor = styles["background-color"];

            if (styles["background-image"])
            {
                cssProps.backgroundImage = `url(${styles["background-image"]})`;
            }
        }
        return cssProps;
    };

    const getControls = () =>
    {
        const gridControls = controls.map((control: CONTROL_TYPES, i) =>
        {
            return (
                <ErrorBoundary key={`ck_${i}`} silent>
                    <GridControl control={control} />
                </ErrorBoundary>
            );
        });

        if (config && config.readMore)
        {
            return (
                <ReadMore height={getReadMoreHeight(config.readMore)}>
                    {gridControls}
                </ReadMore>
            );
        }
        else
        {
            return gridControls;
        }
    };

    const renderArea = () => (
        <div className={getGridSize()}>
            <div className={getColumnConfig()} style={getStyles()}>
                {getControls()}
            </div>
        </div>
    );

    const getAnimation = () =>
    {
        if (config && config.animatecolumn)
        {
            switch (config.animatecolumn)
            {
                case "fadeLeft": return <FadeLeftWrap>{renderArea()}</FadeLeftWrap>;
                case "fadeRight": return <FadeRightWrap>{renderArea()}</FadeRightWrap>;
                default: return renderArea();
            }
        }
        return renderArea();
    };

    const getRender = () =>
    {
        if(config && config.unpublished)
        {
            return null;
        }

        return getAnimation();
    };

    return (
        <ThemeConsumer>
            {theme =>
                <ThemeProvider theme={getRowTheme(theme)}>
                    {getRender()}
                </ThemeProvider>
            }
        </ThemeConsumer>
    );
};

export default GridArea;

